import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, CreditCard, User, Sliders, Shield,
  LogOut, Pause, Trash2, Lock, Save, Loader2, ChevronRight, Moon, Sun,
  Smartphone, Share, Plus, MoreVertical, Download, ToggleLeft, ToggleRight,
  FileCheck, Check, Newspaper, Search, AlertTriangle, Brain,
} from 'lucide-react';
import api from '@/api';
import { SubscriptionTab } from '@/components/settings/SubscriptionTab';
import { ConfirmModal } from '@/components/settings/ConfirmModal';

const TABS = [
  { key: 'subscription', label: 'Subscription', icon: CreditCard },
  { key: 'profile', label: 'Profile', icon: User },
  { key: 'preferences', label: 'Preferences', icon: Sliders },
  { key: 'advisory', label: 'Advisory', icon: FileCheck },
  { key: 'account', label: 'Account', icon: Shield },
];

// Keep these in sync with DisclaimerScreen ITEMS — same wording shown to the
// user at consent time must be re-viewable here after consent.
const ADVISORY_ITEMS = [
  { Icon: Newspaper,      color: '#3B82F6', text: 'Everything you see here is sourced from publicly available data. Every item links back to its original source — tap any trend to see where it came from.' },
  { Icon: Search,         color: '#0891B2', text: "We aggregate and surface trends — we don't alter, interpret, or editorialize the underlying data in any way." },
  { Icon: AlertTriangle,  color: '#F59E0B', text: 'WTFHappened is not a financial advisory service and is not regulated as one. Nothing here is financial advice.' },
  { Icon: Brain,          color: '#DB2777', text: 'Always do your own research before making any financial or investment decision.' },
  { Icon: Shield,         color: '#059669', text: 'We accept no liability for any losses arising from decisions made using this app.' },
];

export default function SettingsPage({ user, onBack, onLogout, onUserUpdate, onEditPreferences }) {
  const [tab, setTab] = useState('subscription');
  const [subInfo, setSubInfo] = useState(null);
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [mobile, setMobile] = useState(user?.mobile || '');
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const [payments, setPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [darkMode, setDarkMode] = useState(() => document.body.classList.contains('dark'));
  const [pricing, setPricing] = useState(null);

  useEffect(() => { loadSubInfo(); loadPricing(); }, []);

  const loadSubInfo = async () => { try { setSubInfo(await api.getSubscriptionInfo()); } catch {} };
  const loadPricing = async () => { try { setPricing(await api.getSubscriptionPricing()); } catch {} };

  const loadPaymentHistory = async () => {
    setLoadingPayments(true);
    try { setPayments((await api.getPaymentHistory()).transactions || []); } catch {} finally { setLoadingPayments(false); }
  };

  const showMsg = (msg) => { setMessage(msg); setError(''); setTimeout(() => setMessage(''), 3000); };
  const showErr = (msg) => { setError(msg); setMessage(''); setTimeout(() => setError(''), 3000); };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const res = await api.updateProfile({ name: name.trim(), email: email.trim() || undefined, mobile: mobile.trim() || undefined });
      if (onUserUpdate) onUserUpdate(res.user);
      showMsg('Profile updated');
    } catch (e) { showErr(e.response?.data?.detail || 'Failed to update'); } finally { setSaving(false); }
  };

  const handleChangePassword = async () => {
    if (newPw.length < 6) { showErr('Password must be at least 6 characters'); return; }
    setSaving(true);
    try {
      await api.changePassword({ current_password: currentPw, new_password: newPw });
      setCurrentPw(''); setNewPw('');
      showMsg('Password changed');
    } catch (e) { showErr(e.response?.data?.detail || 'Failed to change password'); } finally { setSaving(false); }
  };

  const handleToggleAutoRenew = async () => {
    try { const res = await api.toggleAutoRenew(); setSubInfo(prev => ({ ...prev, auto_renew: res.auto_renew })); showMsg(`Auto-renew ${res.auto_renew ? 'enabled' : 'disabled'}`); } catch { showErr('Failed to toggle auto-renew'); }
  };

  const handleSubscribe = async () => {
    try { const res = await api.createCheckout(window.location.origin); window.location.href = res.url; } catch { showErr('Failed to start checkout'); }
  };

  const handleCancelSubscription = async () => {
    setSaving(true);
    try {
      await api.cancelSubscription();
      showMsg('Subscription cancelled');
      setConfirmAction(null);
      loadSubInfo();
      if (onUserUpdate) onUserUpdate({ ...user, subscription_status: 'cancelled' });
    } catch (e) { showErr(e.response?.data?.detail || 'Failed to cancel'); } finally { setSaving(false); }
  };

  const handleSuspend = async () => { try { await api.suspendAccount(); onLogout(); } catch { showErr('Failed'); } };
  const handleDelete = async () => { try { await api.deleteAccount(); onLogout(); } catch { showErr('Failed'); } };

  const handleToggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    document.body.classList.toggle('dark', newMode);
    localStorage.setItem('wtf_dark_mode', newMode ? 'true' : 'false');
  };

  return (
    <div data-testid="settings-page" className="min-h-screen pb-24" style={{ background: '#000' }}>
      <div className="px-5 pt-5 pb-3">
        <div className="flex items-center gap-3 mb-5">
          <button data-testid="settings-back" onClick={onBack}
            className="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
            style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.10)' }}>
            <ArrowLeft size={20} className="text-white" strokeWidth={2.5} />
          </button>
          <h1 className="text-3xl font-black text-white tracking-tight"
              style={{ fontFamily: 'Unbounded, sans-serif' }}>Settings</h1>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {TABS.map(t => {
            const isActive = tab === t.key;
            return (
              <button key={t.key} data-testid={`settings-tab-${t.key}`} onClick={() => setTab(t.key)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-black uppercase tracking-wide whitespace-nowrap transition-all"
                style={{
                  background: isActive ? '#FFFFFF' : '#1A1A1A',
                  color: isActive ? '#000000' : '#E5E7EB',
                  border: isActive ? '2px solid #FFFFFF' : '1.5px solid rgba(255,255,255,0.18)',
                  fontFamily: 'Manrope, sans-serif',
                }}>
                <t.icon size={16} strokeWidth={2.5} />{t.label}
              </button>
            );
          })}
        </div>
      </div>

      <AnimatePresence>
        {message && <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
          className="mx-5 mb-3 px-4 py-2.5 rounded-xl text-sm font-bold text-center"
          style={{ background: 'rgba(16,185,129,0.15)', color: '#34D399', border: '1px solid rgba(16,185,129,0.30)' }}>{message}</motion.div>}
        {error && <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
          className="mx-5 mb-3 px-4 py-2.5 rounded-xl text-sm font-bold text-center"
          style={{ background: 'rgba(239,68,68,0.15)', color: '#F87171', border: '1px solid rgba(239,68,68,0.30)' }}>{error}</motion.div>}
      </AnimatePresence>

      <div className="px-5 mt-3">
        <AnimatePresence mode="wait">
          {tab === 'subscription' && (
            <SubscriptionTab subInfo={subInfo} user={user} showPaymentHistory={showPaymentHistory}
              setShowPaymentHistory={setShowPaymentHistory} payments={payments} loadingPayments={loadingPayments}
              onOpenPaymentHistory={() => { setShowPaymentHistory(true); loadPaymentHistory(); }}
              onSubscribe={handleSubscribe} onToggleAutoRenew={handleToggleAutoRenew}
              onCancelClick={() => setConfirmAction('cancel_sub')} pricing={pricing} />
          )}

          {tab === 'profile' && (
            <motion.div key="profile" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
              <div className="rounded-2xl p-5 space-y-3" style={{ background: '#0F0F0F', border: '1px solid rgba(255,255,255,0.10)' }}>
                <p className="text-[12px] font-black tracking-widest uppercase mb-2"
                   style={{ color: '#FACC15', fontFamily: 'Manrope, sans-serif' }}>Personal Info</p>
                <div><label className="text-[13px] font-bold text-white/90 mb-1.5 block" style={{ fontFamily: 'Manrope, sans-serif' }}>Name</label>
                  <input data-testid="profile-name" data-custom-border="" value={name} onChange={e => setName(e.target.value)}
                    className="w-full px-3.5 py-3 rounded-xl text-[15px] font-semibold text-white outline-none transition-all"
                    style={{ background: '#0A0A0A', border: '1.5px solid rgba(255,255,255,0.18)', fontFamily: 'Manrope, sans-serif' }} /></div>
                <div><label className="text-[13px] font-bold text-white/90 mb-1.5 block" style={{ fontFamily: 'Manrope, sans-serif' }}>Email</label>
                  <input data-testid="profile-email" data-custom-border="" value={email} onChange={e => setEmail(e.target.value)}
                    className="w-full px-3.5 py-3 rounded-xl text-[15px] font-semibold text-white outline-none transition-all"
                    style={{ background: '#0A0A0A', border: '1.5px solid rgba(255,255,255,0.18)', fontFamily: 'Manrope, sans-serif' }} /></div>
                <div><label className="text-[13px] font-bold text-white/90 mb-1.5 block" style={{ fontFamily: 'Manrope, sans-serif' }}>Mobile</label>
                  <input data-testid="profile-mobile" data-custom-border="" value={mobile} onChange={e => setMobile(e.target.value)}
                    className="w-full px-3.5 py-3 rounded-xl text-[15px] font-semibold text-white outline-none transition-all"
                    style={{ background: '#0A0A0A', border: '1.5px solid rgba(255,255,255,0.18)', fontFamily: 'Manrope, sans-serif' }} /></div>
                <button data-testid="profile-save" onClick={handleSaveProfile} disabled={saving}
                  className="w-full py-3.5 rounded-xl text-[14px] font-black flex items-center justify-center gap-2 disabled:opacity-60 transition-all uppercase tracking-wide"
                  style={{ background: '#FFFFFF', color: '#000', fontFamily: 'Manrope, sans-serif' }}>
                  {saving ? <Loader2 size={16} className="animate-spin" /> : <><Save size={16} strokeWidth={2.5} /> Save Changes</>}
                </button>
              </div>
              <div className="rounded-2xl p-5 space-y-3" style={{ background: '#0F0F0F', border: '1px solid rgba(255,255,255,0.10)' }}>
                <p className="text-[12px] font-black tracking-widest uppercase mb-2"
                   style={{ color: '#FACC15', fontFamily: 'Manrope, sans-serif' }}>Change Password</p>
                <input data-testid="profile-current-pw" data-custom-border="" type="password" value={currentPw} onChange={e => setCurrentPw(e.target.value)} placeholder="Current password"
                  className="w-full px-3.5 py-3 rounded-xl text-[15px] font-semibold text-white placeholder-white/40 outline-none transition-all"
                  style={{ background: '#0A0A0A', border: '1.5px solid rgba(255,255,255,0.18)', fontFamily: 'Manrope, sans-serif' }} />
                <input data-testid="profile-new-pw" data-custom-border="" type="password" value={newPw} onChange={e => setNewPw(e.target.value)} placeholder="New password (min 6 chars)"
                  className="w-full px-3.5 py-3 rounded-xl text-[15px] font-semibold text-white placeholder-white/40 outline-none transition-all"
                  style={{ background: '#0A0A0A', border: '1.5px solid rgba(255,255,255,0.18)', fontFamily: 'Manrope, sans-serif' }} />
                <button data-testid="profile-change-pw" onClick={handleChangePassword} disabled={saving || !currentPw || !newPw}
                  className="w-full py-3.5 rounded-xl text-[14px] font-black flex items-center justify-center gap-2 disabled:opacity-50 transition-all uppercase tracking-wide"
                  style={{ background: '#FFFFFF', color: '#000', fontFamily: 'Manrope, sans-serif' }}>
                  <Lock size={16} strokeWidth={2.5} /> Change Password
                </button>
              </div>
            </motion.div>
          )}

          {tab === 'preferences' && (
            <motion.div key="prefs" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
              <div className="rounded-2xl p-5" style={{ background: '#0F0F0F', border: '1px solid rgba(255,255,255,0.10)' }}>
                <p className="text-[12px] font-black tracking-widest uppercase mb-3"
                   style={{ color: '#FACC15', fontFamily: 'Manrope, sans-serif' }}>Appearance</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {darkMode ? <Moon size={20} className="text-white/80" /> : <Sun size={20} className="text-white/80" />}
                    <div>
                      <p className="text-[15px] font-black text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>Dark Mode</p>
                      <p className="text-[12px] font-semibold text-white/70" style={{ fontFamily: 'Manrope, sans-serif' }}>{darkMode ? 'Dark theme active' : 'Light theme active'}</p>
                    </div>
                  </div>
                  <button data-testid="dark-mode-toggle" onClick={handleToggleDarkMode} className="text-white">
                    {darkMode ? <ToggleRight size={36} className="text-emerald-400" /> : <ToggleLeft size={36} className="text-white/30" />}
                  </button>
                </div>
              </div>
              <button data-testid="edit-preferences-btn" onClick={onEditPreferences}
                className="w-full rounded-2xl p-5 flex items-center gap-3 text-left transition-colors"
                style={{ background: '#0F0F0F', border: '1px solid rgba(250,204,21,0.40)' }}>
                <Sliders size={20} style={{ color: '#FACC15' }} strokeWidth={2.5} />
                <div className="flex-1">
                  <p className="text-[15px] font-black text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>Your Preferences</p>
                  <p className="text-[12px] font-semibold text-white/70 mt-0.5" style={{ fontFamily: 'Manrope, sans-serif' }}>Edit interests, curiosity types, depth, location & more</p>
                </div>
                <ChevronRight size={18} className="text-white/60" strokeWidth={2.5} />
              </button>
            </motion.div>
          )}

          {tab === 'advisory' && (
            <motion.div key="advisory" data-testid="advisory-tab" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
              {/* Consent status — immutable, cannot be unticked */}
              <div className="rounded-2xl p-5" style={{ background: '#0F0F0F', border: '1px solid rgba(255,255,255,0.10)' }}>
                <p className="text-[12px] font-black tracking-widest uppercase mb-3"
                   style={{ color: '#FACC15', fontFamily: 'Manrope, sans-serif' }}>Consent to advisory</p>
                {user?.advisory_consent_at ? (
                  <div data-testid="advisory-consent-row" className="flex items-start gap-3 p-3.5 rounded-xl"
                       style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}>
                    <div className="flex-shrink-0 w-6 h-6 rounded-md bg-emerald-500 flex items-center justify-center mt-0.5" title="Consent locked — cannot be revoked">
                      <Check size={14} className="text-white" strokeWidth={3} />
                    </div>
                    <div className="flex-1">
                      <p className="text-[15px] font-black text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>Advisory terms accepted</p>
                      <p className="text-[12px] font-semibold text-white/80 mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        You acknowledged these terms on{' '}
                        <span className="font-black text-white">
                          {new Date(user.advisory_consent_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                        </span>
                        {' at '}
                        <span className="font-black text-white">
                          {new Date(user.advisory_consent_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        {user.advisory_consent_version && <> (v{user.advisory_consent_version})</>}
                        .
                      </p>
                      <p className="text-[11px] font-semibold text-white/60 mt-1.5 italic">
                        Required for using the app. This record is permanent and cannot be revoked.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-3 p-3.5 rounded-xl"
                       style={{ background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.35)' }}>
                    <AlertTriangle size={18} className="text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[15px] font-black text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>Advisory not yet acknowledged</p>
                      <p className="text-[12px] font-semibold text-white/80 mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>You'll be asked to accept the advisory the next time you open the app.</p>
                    </div>
                  </div>
                )}
              </div>

              {/* The advisory text itself — always viewable here so users can re-read it any time */}
              <div className="rounded-2xl p-5" style={{ background: '#0F0F0F', border: '1px solid rgba(255,255,255,0.10)' }}>
                <p className="text-[12px] font-black tracking-widest uppercase mb-4"
                   style={{ color: '#FACC15', fontFamily: 'Manrope, sans-serif' }}>What you accepted</p>
                <div className="space-y-4">
                  {ADVISORY_ITEMS.map((item, i) => (
                    <div key={i} className="flex gap-3 items-start">
                      <div className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center mt-0.5"
                        style={{ background: `${item.color}20`, border: `1px solid ${item.color}40` }}>
                        <item.Icon size={16} style={{ color: item.color }} strokeWidth={2.5} />
                      </div>
                      <p className="text-[14px] font-semibold text-white/90 leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>{item.text}</p>
                    </div>
                  ))}
                </div>
                <p className="text-[12px] font-semibold text-white/60 italic mt-5 pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                  Full legal terms are available in our Terms of Service.
                </p>
              </div>
            </motion.div>
          )}

          {tab === 'account' && (
            <motion.div key="account" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-3">
              <div data-testid="install-instructions" className="rounded-2xl p-5"
                   style={{ background: '#0F0F0F', border: '1px solid rgba(255,255,255,0.10)' }}>
                <div className="flex items-center gap-2 mb-3">
                  <Smartphone size={20} className="text-blue-400" strokeWidth={2.5} />
                  <p className="text-[15px] font-black text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>Save to Home Screen</p>
                </div>
                <p className="text-[13px] font-semibold text-white/80 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Access WTFHappened like a native app from your phone's home screen.</p>
                {/iphone|ipad|ipod/i.test(navigator.userAgent) ? (
                  <div className="space-y-2 text-[13px] font-semibold text-white/85" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <div className="flex items-center gap-2"><Share size={15} className="text-blue-400 shrink-0" strokeWidth={2.5} /> Tap the Share button in Safari</div>
                    <div className="flex items-center gap-2"><Plus size={15} className="text-emerald-400 shrink-0" strokeWidth={2.5} /> Tap "Add to Home Screen"</div>
                    <div className="flex items-center gap-2"><Smartphone size={15} className="text-purple-400 shrink-0" strokeWidth={2.5} /> Tap "Add" to confirm</div>
                  </div>
                ) : /android/i.test(navigator.userAgent) ? (
                  <div className="space-y-2 text-[13px] font-semibold text-white/85" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <div className="flex items-center gap-2"><MoreVertical size={15} className="text-blue-400 shrink-0" strokeWidth={2.5} /> Tap the 3-dot menu in Chrome</div>
                    <div className="flex items-center gap-2"><Download size={15} className="text-emerald-400 shrink-0" strokeWidth={2.5} /> Tap "Install app" or "Add to Home screen"</div>
                    <div className="flex items-center gap-2"><Smartphone size={15} className="text-purple-400 shrink-0" strokeWidth={2.5} /> Confirm install</div>
                  </div>
                ) : (
                  <div className="space-y-2 text-[13px] font-semibold text-white/85" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <div className="flex items-center gap-2"><Download size={15} className="text-blue-400 shrink-0" strokeWidth={2.5} /> Look for the install icon in Chrome's address bar</div>
                    <div className="flex items-center gap-2"><Smartphone size={15} className="text-purple-400 shrink-0" strokeWidth={2.5} /> Or go to Menu &#8594; "Install WTFHappened"</div>
                  </div>
                )}
                <button onClick={() => { localStorage.removeItem('wtf_install_confirmed'); sessionStorage.removeItem('wtf_install_dismissals'); setMessage('Install prompt will show next time you open the app'); setTimeout(() => setMessage(''), 3000); }}
                  className="mt-4 text-[13px] font-black text-blue-400 hover:text-blue-300 uppercase tracking-wide" style={{ fontFamily: 'Manrope, sans-serif' }}>Show install prompt again</button>
              </div>
              <button data-testid="account-signout" onClick={onLogout}
                className="w-full rounded-2xl p-4 flex items-center gap-3 text-left transition-colors hover:brightness-110"
                style={{ background: '#0F0F0F', border: '1px solid rgba(255,255,255,0.10)' }}>
                <LogOut size={20} className="text-white/80" strokeWidth={2.5} />
                <div className="flex-1">
                  <p className="text-[15px] font-black text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>Sign Out</p>
                  <p className="text-[12px] font-semibold text-white/70" style={{ fontFamily: 'Manrope, sans-serif' }}>Log out of your account</p>
                </div>
                <ChevronRight size={18} className="text-white/60" strokeWidth={2.5} />
              </button>
              <button data-testid="account-suspend" onClick={() => setConfirmAction('suspend')}
                className="w-full rounded-2xl p-4 flex items-center gap-3 text-left transition-colors hover:brightness-110"
                style={{ background: '#0F0F0F', border: '1px solid rgba(250,204,21,0.30)' }}>
                <Pause size={20} className="text-amber-400" strokeWidth={2.5} />
                <div className="flex-1">
                  <p className="text-[15px] font-black text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>Suspend Account</p>
                  <p className="text-[12px] font-semibold text-white/70" style={{ fontFamily: 'Manrope, sans-serif' }}>Temporarily disable your account</p>
                </div>
                <ChevronRight size={18} className="text-white/60" strokeWidth={2.5} />
              </button>
              <button data-testid="account-delete" onClick={() => setConfirmAction('delete')}
                className="w-full rounded-2xl p-4 flex items-center gap-3 text-left transition-colors hover:brightness-110"
                style={{ background: '#0F0F0F', border: '1px solid rgba(239,68,68,0.40)' }}>
                <Trash2 size={20} className="text-red-400" strokeWidth={2.5} />
                <div className="flex-1">
                  <p className="text-[15px] font-black text-red-400" style={{ fontFamily: 'Manrope, sans-serif' }}>Delete Account</p>
                  <p className="text-[12px] font-semibold text-white/70" style={{ fontFamily: 'Manrope, sans-serif' }}>Permanently remove your account and data</p>
                </div>
                <ChevronRight size={18} className="text-white/60" strokeWidth={2.5} />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          <ConfirmModal confirmAction={confirmAction} saving={saving} onClose={() => setConfirmAction(null)}
            onCancelSubscription={handleCancelSubscription} onSuspend={handleSuspend} onDelete={handleDelete} />
        </AnimatePresence>
      </div>
    </div>
  );
}
